import { Badge } from "reactstrap";
import AppCustomView from "../../../components/AppCustomView/AppCustomView";
import useModule from "../../../hooks/useModule";
import { Query } from "../../../types/GlobalInterfaces";
import { ViewTypes } from "../../../types/PageInterfaces";
import CustomToggle from "../../../components/Drawers/Fields/CustomToggle";
import { useAtomValue } from "jotai";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useCloud from "../../../api/api-hooks/cloud/useCloud";
import { useEffect, useState } from "react";
import useUpdateCloud from "../../../api/api-hooks/cloud/useUpdateCloud";
import { hideToast, newToastEvent } from "../../../utils/ToastUtil";
import { captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";

export interface CustomViewToolbar {
  Search: {
    Enabled: boolean;
    Dictation: boolean;
    QuickSearchFields: string[];
  };
  Filters: {
    Enabled: boolean;
  };
  Sort: {
    Enabled: boolean;
  };
  CloudPermissions?: string;
}

export interface CustomViewConfig {
  Type: ViewTypes;
  ModuleAPIName: string;
  Query: Query;
  CardsLayouts?: any[];
}

const AIModelsToolbar: CustomViewToolbar = {
  Search: {
    Enabled: true,
    Dictation: true,
    QuickSearchFields: ["_RecordName", "Company"],
  },
  Filters: {
    Enabled: true,
  },
  Sort: {
    Enabled: true,
  },
};

const AIModelsView = {
  Type: "Cards" as ViewTypes,
  ModuleAPIName: "Global.AIModels",
  Query: {
    OrderBy: {
      Field: "Created",
      Direction: "DESC",
    },
    Pagination: {
      Count: 25,
      Page: 1,
    },
  } as Query,
  CardsLayouts: [
    {
      Name: "Cards",
      Fields: ["id", "_RecordName", "Description"],
      CardType: "Image",
      CardImage: "Image",
    },
  ],
};

export default function AIModels() {
  const tenantID = useAtomValue(tenantIDAtom);
  const { module } = useModule("AIModels");
  const { cloud, cloudLoading } = useCloud(tenantID);
  const { updateCloud } = useUpdateCloud();
  const client = useQueryClient();

  const [hiddenModels, setHiddenModels] = useState<string[]>([]);

  useEffect(() => {
    if (!Array.isArray(cloud?.HiddenAIModels)) return;

    setHiddenModels(cloud.HiddenAIModels);
  }, [cloud?.HiddenAIModels]);

  if (!module) return null;

  return (
    <>
      <AppCustomView
        toolbarConfig={AIModelsToolbar}
        viewConfig={AIModelsView}
        moduleSchema={module}
        CustomCardFooter={({ record }: { record: Record<string, any> }) => (
          <AIModelsFooter
            record={record}
            isHidden={hiddenModels.includes(record?.ModelAPIName)}
            onToggleHide={() => toggleAIModel(record?.ModelAPIName)}
            cloudLoading={cloudLoading}
          />
        )}
      />
    </>
  );

  function toggleAIModel(modelAPIName: string) {
    if (!tenantID) {
      captureException("Cloud not found! Can't update cloud settings.");
      return newToastEvent(
        "error",
        "Cloud not found! Can't update cloud settings."
      );
    }

    let newHiddenModels = [...hiddenModels];

    if (newHiddenModels.includes(modelAPIName)) {
      newHiddenModels = newHiddenModels.filter((m) => m !== modelAPIName);
    } else {
      newHiddenModels = [...newHiddenModels, modelAPIName];
    }

    const toastID = `updating-cloud-${tenantID}`;

    newToastEvent("success", "Updating Cloud...", toastID, {
      loading: true,
      autoClose: false,
    });

    const timeoutID = setTimeout(() => {
      hideToast(toastID);
      newToastEvent("error", "Failed to update cloud. Request timed out.");
    }, 10000);

    setHiddenModels(newHiddenModels);
    updateCloud({
      tenantID: tenantID,
      payload: {
        HiddenAIModels: newHiddenModels,
      },
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["BrieGPTModels", tenantID] });
        clearTimeout(timeoutID);
        hideToast(toastID);
      },
    });
  }
}

function AIModelsFooter({
  record,
  isHidden,
  onToggleHide,
  cloudLoading,
}: {
  record: Record<string, any>;
  isHidden: boolean;
  onToggleHide: () => void;
  cloudLoading: boolean;
}) {
  const text =
    record?.Company === "Privately Hosted" ? "Privately Hosted" : "3rd Party";

  return (
    <div className="tw:flex tw:px-2 tw:pb-2 tw:w-full tw:justify-between tw:items-start">
      <Badge color="medium" className="tw:py-1.5">
        {text}
      </Badge>
      {!cloudLoading && (
        <CustomToggle
          value={!isHidden}
          handleChange={onToggleHide}
          containerCSS="tw:mb-0"
          checkedOption="Show"
          unCheckedOption="Hide"
        />
      )}
    </div>
  );
}
